export function initScrollSpyActiveNav({
  doc = document,
  win = window,

  // when to enable
  desktopMin = 1024,

  // scroll line position = sticky header + buffer
  headerOffset = 72,
  buffer = 100,

  // where nav lives
  asideSelector = '.faq-page__asside',
  navLinkSelector = '.faq-page__nav-link',
  navItemSelector = '.faq-page__nav-item',

  // active class
  activeClass = 'active',

  respectHashOnInit = true,
} = {}) {
  const prefersReducedMotion = () =>
    win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () =>
    win.matchMedia
      ? win.matchMedia(`(min-width: ${desktopMin}px)`).matches
      : win.innerWidth >= desktopMin;

  const getAll = (selector, root = doc) => Array.from(root.querySelectorAll(selector));
  const getOne = (selector, root = doc) => root.querySelector(selector);

  const asideEl = getOne(asideSelector, doc);
  if (!asideEl) return;

  const guardKey = '__scrollSpyInited';
  if (asideEl[guardKey]) return;
  asideEl[guardKey] = true;

  const navLinksEls = getAll(navLinkSelector, asideEl);
  const navHashes = navLinksEls
    .map((a) => (a.getAttribute('href') || '').trim())
    .filter((href) => href.startsWith('#') && href.length > 1);

  const targets = navHashes
    .map((hash) => doc.getElementById(hash.slice(1)))
    .filter(Boolean);

  if (!targets.length) return;

  const setActiveNavLink = (hash) => {
    const links = navLinksEls.length ? navLinksEls : getAll(navLinkSelector, asideEl);
    const items = getAll(navItemSelector, asideEl);

    items.forEach((it) => it.classList.remove(activeClass));
    links.forEach((a) => a.classList.remove(activeClass));

    if (!hash) return;

    const activeLink = links.find((a) => (a.getAttribute('href') || '') === hash);
    if (!activeLink) return;

    activeLink.classList.add(activeClass);
    activeLink.closest(navItemSelector)?.classList.add(activeClass);
  };

  const ACTIVE_OFFSET = headerOffset + buffer;
  let lastHash = '';

  const activate = (hash) => {
    if (!hash || hash === lastHash) return;
    lastHash = hash;
    setActiveNavLink(hash);
  };

  const getTop = (el) => el.getBoundingClientRect().top + win.pageYOffset;

  const focusTarget = (target) => {
    const focusEl = target.querySelector('.faq-page__block-header') || target;

    if (!focusEl.hasAttribute('tabindex')) {
      focusEl.setAttribute('tabindex', '-1');
      focusEl.dataset.scrollSpyAddedTabindex = 'true';
    }

    focusEl.focus({ preventScroll: true });
  };

  const scrollToHash = (hash) => {
    const id = String(hash || '').replace('#', '');
    if (!id) return;

    const target = doc.getElementById(id);
    if (!target) return;

    const top = getTop(target) - headerOffset;

    win.scrollTo({
      top,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    focusTarget(target);
  };

  // rAF throttle for smoother updates on desktop
  let ticking = false;
  const updateActiveByScroll = () => {
    ticking = false;
    if (!isDesktop()) return;

    const triggerLine = win.scrollY + ACTIVE_OFFSET;

    let current = null;
    for (const el of targets) {
      if (getTop(el) <= triggerLine) current = el;
      else break;
    }

    if (!current && targets[0]) current = targets[0];
    if (current?.id) activate(`#${current.id}`);
  };

  const onScroll = () => {
    if (!isDesktop()) return;
    if (ticking) return;
    ticking = true;
    win.requestAnimationFrame(updateActiveByScroll);
  };

  // Smooth anchor scroll on desktop (prevents instant native jump)
  const clickGuardKey = '__scrollSpyClickBound';
  if (!asideEl[clickGuardKey]) {
    asideEl[clickGuardKey] = true;

    asideEl.addEventListener('click', (e) => {
      if (!isDesktop()) return;

      const a = e.target?.closest?.(navLinkSelector);
      if (!a || !asideEl.contains(a)) return;

      const href = (a.getAttribute('href') || '').trim();
      if (!href.startsWith('#') || href.length <= 1) return;

      e.preventDefault();

      // immediately set active, then smooth scroll
      activate(href);
      scrollToHash(href);

      // optional: keep URL hash in sync without triggering native jump
      if (win.history && win.history.pushState) {
        win.history.pushState(null, '', href);
      } else {
        win.location.hash = href;
      }
    });
  }

  // INIT ACTIVE
  if (respectHashOnInit && win.location.hash) {
    activate(win.location.hash);
  } else {
    const firstId = targets[0]?.id;
    if (firstId) activate(`#${firstId}`);
  }

  // sync with current scroll position
  onScroll();

  // listeners
  win.addEventListener('scroll', onScroll, { passive: true });
  win.addEventListener('resize', onScroll);

  win.addEventListener('hashchange', () => {
    if (!isDesktop()) return;

    const hash = win.location.hash;
    if (hash) {
      activate(hash);
      // if hash changed by something else, scroll smoothly too
      scrollToHash(hash);
    }

    if (!prefersReducedMotion()) onScroll();
  });
}
